import { useState, useMemo, useCallback, useEffect } from "react"
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  MinusCircleIcon,
  PhotoIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import { formatNumber } from "~/ui-rtk/utils/format"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type {
  AnalyzeCreativesResponse,
  CandidateDecisionInput,
  CandidateSignals,
  CreativeCandidate,
  CreativePlaybook,
  ExecuteRowResult,
} from "../types"

type Phase = "review" | "applying" | "results"

const PLAYBOOK_TITLES: Record<CreativePlaybook, string> = {
  UNDERPERFORMERS: "Archive Underperformers",
  IRRELEVANT: "Archive Irrelevant Ads",
  DUPLICATIVE_LOSERS: "Archive Duplicates",
  TOO_SIMILAR: "Review Similar Ads",
}

const GUARDRAIL_LABELS: Record<string, string> = {
  CLUSTER_KEEPER: "Cluster keeper",
  HIGH_SPEND_PROTECTION: "High spend protection",
  MIN_ACTIVE_FLOOR: "Would breach active-ad floor",
  BATCH_LIMIT: "Batch limit reached",
}

function guardrailLabel(reason: string): string {
  return GUARDRAIL_LABELS[reason] ?? reason
}

function formatShortDate(dateStr: string): string {
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number)
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

function signalChips(signals: CandidateSignals): string[] {
  return [
    `${formatNumber(signals.spend28, { currency: true })} / 28d`,
    `spend share ${(signals.spendShare28 * 100).toFixed(1)}%`,
    `live ${signals.tenureDays}d`,
    signals.lastSpendDate
      ? `last spend ${formatShortDate(signals.lastSpendDate)}`
      : "no recorded spend",
  ]
}

function Thumbnail({ url, alt }: { url: string | null; alt: string }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return (
      <div className="w-12 h-12 rounded bg-basic-black flex items-center justify-center shrink-0">
        <PhotoIcon className="w-5 h-5 text-gray-600" />
      </div>
    )
  }
  return (
    <img
      src={url}
      alt={alt}
      className="w-12 h-12 rounded object-cover shrink-0"
      onError={() => setFailed(true)}
    />
  )
}

interface CandidateGroup {
  key: string
  adsetName: string
  /** LLM cluster explanation — similarity playbooks only */
  explanation?: string
  candidates: CreativeCandidate[]
}

interface ReviewModalProps {
  isOpen: boolean
  playbook: CreativePlaybook | null
  result: AnalyzeCreativesResponse | null
  progress: { done: number; total: number }
  onClose: () => void
  /** Sequentially records every decision (ACCEPTED archives, REJECTED is
   *  recorded as SKIPPED); blocked and keeper rows are never sent. */
  onExecute: (
    decisions: CandidateDecisionInput[],
  ) => Promise<ExecuteRowResult[]>
}

export default function ReviewModal({
  isOpen,
  playbook,
  result,
  progress,
  onClose,
  onExecute,
}: ReviewModalProps) {
  const [phase, setPhase] = useState<Phase>("review")
  const [results, setResults] = useState<ExecuteRowResult[]>([])
  const [checked, setChecked] = useState<Set<string>>(new Set())

  // Re-initialize per analysis run: unblocked candidates start checked EXCEPT
  // high-spend winner variants (explicit opt-in) — and TOO_SIMILAR preselects
  // nothing (the user chooses which near-duplicates to retire).
  useEffect(() => {
    if (!isOpen || !result) return
    const preselect =
      playbook === "TOO_SIMILAR"
        ? new Set<string>()
        : new Set(
            result.candidates
              .filter(
                c =>
                  !c.isKeeper &&
                  !c.guardrails.blocked &&
                  !c.guardrails.winnerVariant,
              )
              .map(c => c.id),
          )
    setChecked(preselect)
    setPhase("review")
    setResults([])
  }, [isOpen, result, playbook])

  const handleClose = useCallback(() => {
    setPhase("review")
    setResults([])
    onClose()
  }, [onClose])

  const handleToggle = useCallback((candidateId: string) => {
    setChecked(prev => {
      const next = new Set(prev)
      if (next.has(candidateId)) {
        next.delete(candidateId)
      } else {
        next.add(candidateId)
      }
      return next
    })
  }, [])

  const isClusterPlaybook =
    playbook === "DUPLICATIVE_LOSERS" || playbook === "TOO_SIMILAR"

  const groups = useMemo((): CandidateGroup[] => {
    if (!result) return []
    if (isClusterPlaybook) {
      const explanationByKey = new Map(
        result.clusters.map(c => [c.key, c.explanation]),
      )
      const byCluster = new Map<string, CreativeCandidate[]>()
      for (const candidate of result.candidates) {
        const key = candidate.clusterKey ?? candidate.adsetExternalId
        let list = byCluster.get(key)
        if (!list) {
          list = []
          byCluster.set(key, list)
        }
        list.push(candidate)
      }
      return [...byCluster.entries()].map(([key, candidates]) => ({
        key,
        adsetName: candidates[0].adsetName,
        explanation: explanationByKey.get(key),
        candidates,
      }))
    }
    const byAdset = new Map<string, CreativeCandidate[]>()
    for (const candidate of result.candidates) {
      let list = byAdset.get(candidate.adsetExternalId)
      if (!list) {
        list = []
        byAdset.set(candidate.adsetExternalId, list)
      }
      list.push(candidate)
    }
    return [...byAdset.entries()].map(([key, candidates]) => ({
      key,
      adsetName: candidates[0].adsetName,
      candidates,
    }))
  }, [result, isClusterPlaybook])

  const archiveCount = checked.size

  const handleConfirm = useCallback(async () => {
    if (!result) return
    const decisions: CandidateDecisionInput[] = []
    for (const candidate of result.candidates) {
      if (candidate.isKeeper || candidate.guardrails.blocked) continue
      decisions.push({
        candidateId: candidate.id,
        decision: checked.has(candidate.id) ? "ACCEPTED" : "REJECTED",
        adName: candidate.adName,
        adsetName: candidate.adsetName,
      })
    }
    setPhase("applying")
    const executeResults = await onExecute(decisions)
    setResults(executeResults)
    setPhase("results")
  }, [result, checked, onExecute])

  const resultsSummary = useMemo(() => {
    const archived = results.filter(r => r.outcome === "ARCHIVED").length
    const failed = results.filter(r => r.outcome === "FAILED").length
    const skipped = results.filter(r => r.outcome === "SKIPPED").length
    return { archived, failed, skipped }
  }, [results])

  const renderCandidateRow = (candidate: CreativeCandidate) => {
    const { guardrails } = candidate
    const isSelectable = !candidate.isKeeper && !guardrails.blocked
    return (
      <div
        key={candidate.id}
        className={cn(
          "flex items-start gap-3 px-3 py-2 border-t border-basic-blue first:border-t-0",
          guardrails.blocked && "opacity-60",
        )}
      >
        <div className="pt-3 w-5 shrink-0">
          {candidate.isKeeper ? (
            <LockClosedIcon className="w-4 h-4 text-gray-400" />
          ) : guardrails.blocked ? (
            <Checkbox checked={false} disabled className="checkbox-sm" />
          ) : (
            <Checkbox
              checked={checked.has(candidate.id)}
              onChange={() => handleToggle(candidate.id)}
              className="checkbox-sm"
            />
          )}
        </div>
        <Thumbnail url={candidate.thumbnailUrl} alt={candidate.adName} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm text-white truncate">
              {candidate.adName}
            </span>
            {candidate.isKeeper && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-600/30 text-blue-300 shrink-0">
                Keeper
              </span>
            )}
            {guardrails.blocked &&
              guardrails.reasons.map(reason => (
                <span
                  key={reason}
                  className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-600/30 text-gray-300 shrink-0"
                >
                  {guardrailLabel(reason)}
                </span>
              ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {signalChips(candidate.signals).map(chip => (
              <span
                key={chip}
                className="text-[11px] px-1.5 py-0.5 rounded bg-basic-black text-gray-400"
              >
                {chip}
              </span>
            ))}
          </div>
          {isSelectable && guardrails.winnerVariant && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <ExclamationTriangleIcon className="w-4 h-4 text-yellow-400 shrink-0" />
              <span className="text-xs text-yellow-300">
                High-spend winner — check the box only if you are sure this ad
                should be archived.
              </span>
            </div>
          )}
          {!candidate.isKeeper && (
            <p className="text-xs text-gray-400 mt-1.5">
              {candidate.rationale}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={
        phase === "applying"
          ? () => {
              /* noop while applying */
            }
          : handleClose
      }
      width={860}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-xl p-0 text-left max-h-[85vh] flex flex-col",
      }}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-basic-blue">
        <h2 className="text-lg font-semibold text-white">
          {phase === "review" &&
            (playbook ? PLAYBOOK_TITLES[playbook] : "Review Candidates")}
          {phase === "applying" && "Archiving Ads..."}
          {phase === "results" && "Results"}
        </h2>
        {phase === "review" && result && (
          <p className="text-sm text-gray-400 mt-1">
            Data through {formatShortDate(result.dataThrough)} — review each
            proposed ad before archiving.
          </p>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {phase === "review" && result && (
          <>
            {result.llmFallback && (
              <div className="flex items-center gap-2 bg-yellow-900/30 border border-yellow-700 rounded-lg px-4 py-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 shrink-0" />
                <span className="text-yellow-300 text-sm">
                  LLM review was unavailable — rationale below comes from
                  deterministic templates.
                </span>
              </div>
            )}

            {result.candidates.length === 0 && (
              <div className="border border-basic-blue rounded-lg p-8 text-center text-gray-400">
                No archive candidates found in the selected ad sets.
              </div>
            )}

            {groups.map(group => (
              <div
                key={group.key}
                className="border border-basic-blue rounded-lg overflow-hidden"
              >
                <div className="bg-system-blue-800 px-3 py-2">
                  <span className="text-sm font-medium text-white">
                    {group.adsetName}
                  </span>
                  {group.explanation && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {group.explanation}
                    </p>
                  )}
                </div>
                <div className="bg-basic-dark-blue">
                  {group.candidates.map(renderCandidateRow)}
                </div>
              </div>
            ))}

            {result.skippedAdsets.length > 0 && (
              <p className="text-xs text-gray-500">
                {result.skippedAdsets.length}{" "}
                {result.skippedAdsets.length === 1 ? "ad set" : "ad sets"}{" "}
                skipped:{" "}
                {result.skippedAdsets
                  .map(s => `${s.adsetExternalId} (${s.reason})`)
                  .join(", ")}
              </p>
            )}
          </>
        )}

        {phase === "applying" && (
          <div className="py-8 space-y-4">
            <div className="w-full bg-basic-black rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                style={{
                  width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%`,
                }}
              />
            </div>
            <p className="text-center text-sm text-gray-400">
              Recording decisions... {progress.done}/{progress.total}
            </p>
          </div>
        )}

        {phase === "results" && (
          <div>
            <p className="text-sm text-gray-400 mb-3">
              {resultsSummary.archived} archived
              {resultsSummary.failed > 0 && (
                <span className="text-states-error">
                  , {resultsSummary.failed} failed
                </span>
              )}
              {resultsSummary.skipped > 0 && (
                <span>, {resultsSummary.skipped} skipped</span>
              )}
            </p>
            <div className="space-y-1 max-h-[320px] overflow-y-auto">
              {results.map(r => (
                <div
                  key={r.candidateId}
                  className="flex items-center gap-2 text-sm py-1"
                >
                  {r.outcome === "ARCHIVED" ? (
                    <CheckCircleIcon className="w-4 h-4 text-american-green shrink-0" />
                  ) : r.outcome === "FAILED" ? (
                    <XCircleIcon className="w-4 h-4 text-states-error shrink-0" />
                  ) : (
                    <MinusCircleIcon className="w-4 h-4 text-gray-500 shrink-0" />
                  )}
                  <span className="text-white truncate">{r.adName}</span>
                  <span className="text-xs text-gray-500">{r.adsetName}</span>
                  {r.error && (
                    <span className="text-xs text-states-error ml-auto">
                      {r.error}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-basic-blue">
        {phase === "review" && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-gray-500">
              Archiving is one-way on Meta, but each ad&apos;s post and creative
              are preserved in the registry for rebuilding.
            </span>
            <div className="flex items-center gap-3 shrink-0">
              <Button
                variant={{ variant: "outlined" }}
                size="sm"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant={{ variant: "solid", color: "red" }}
                size="sm"
                disabled={archiveCount === 0}
                onClick={handleConfirm}
              >
                Archive {archiveCount} {archiveCount === 1 ? "ad" : "ads"}
              </Button>
            </div>
          </div>
        )}
        {phase === "applying" && (
          <div className="flex justify-end">
            <Button variant={{ variant: "outlined" }} size="sm" disabled>
              Applying...
            </Button>
          </div>
        )}
        {phase === "results" && (
          <div className="flex justify-end">
            <Button
              variant={{ variant: "solid", color: "blue" }}
              size="sm"
              onClick={handleClose}
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  )
}
